"use client";

import { useAuth } from "@/components/auth-provider";
import { ChooseCompanionModal } from "@/components/character/choose-companion-modal";
import { useSubscription } from "@/components/subscriptions/hooks/use-subscription";
import { Button } from "@/components/ui/button";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";
import { useSearchState } from "@/hooks/use-search-state";
import { cn } from "@/lib/utils";
import { IconSearch } from "@tabler/icons-react";
import { useCallback, useRef, useState } from "react";
import useConversationsInfiniteQuery from "../../../hooks/use-conversations-infinite-query";
import { ConversationsMenuEmptyState } from "./conversations-menu-empty-state";
import {
  ConversationsMenuItem,
  ConversationsMenuItemSkeleton,
} from "./conversations-menu-item";

export function ConversationsMenuContent() {
  const { claims } = useAuth();
  const { subscription } = useSubscription();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { inputValue, search, onSearch } = useSearchState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    conversations,
    fetchNextPageConversations,
    hasNextPageConversations,
    isLoadingConversations,
    isFetchingNextPageConversations,
  } = useConversationsInfiniteQuery({
    searchTerm: search,
    enabled: Boolean(claims?.sub),
  });

  const loadMoreConversations = useCallback(() => {
    if (!isFetchingNextPageConversations && hasNextPageConversations) {
      fetchNextPageConversations();
    }
  }, [
    isFetchingNextPageConversations,
    hasNextPageConversations,
    fetchNextPageConversations,
  ]);

  const { targetElementRef } = useIntersectionObserver<HTMLDivElement>(
    loadMoreConversations,
    hasNextPageConversations,
    isFetchingNextPageConversations,
  );

  return (
    <>
      <div className="border-border shrink-0 border-b">
        <div className="p-4">
          <div className="flex h-10 items-center justify-between">
            <h1 className="text-xl font-semibold">Chat</h1>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => {
                  setIsSearchOpen(!isSearchOpen);
                  if (!isSearchOpen) {
                    inputRef.current?.focus();
                  }
                }}
                variant="ghost"
                iconOnly
                size="md"
                icon={<IconSearch className="h-5 w-5" />}
                aria-label="Toggle search"
              />
              <ChooseCompanionModal />
            </div>
          </div>
        </div>
        <div
          className={cn(
            "overflow-hidden transition-all duration-300 ease-in-out",
            {
              "max-h-0 opacity-0": !isSearchOpen,
              "max-h-20 opacity-100": isSearchOpen,
            },
          )}
        >
          <div className="p-4">
            <InputGroup className="h-10 bg-white">
              <InputGroupAddon align="inline-start">
                <IconSearch className="h-4 w-4" />
              </InputGroupAddon>
              <InputGroupInput
                ref={inputRef}
                type="search"
                placeholder="Search conversations..."
                autoFocus={isSearchOpen}
                autoComplete="off"
                autoCorrect="off"
                data-form-type="other"
                value={inputValue}
                onChange={(e) => onSearch(e.target.value)}
              />
            </InputGroup>
          </div>
        </div>
      </div>
      {!isLoadingConversations && conversations.length === 0 ? (
        <ConversationsMenuEmptyState />
      ) : (
        <ScrollArea className="min-h-0 flex-1">
          {conversations.map((conversation) => (
            <ConversationsMenuItem
              key={conversation.id}
              conversation={conversation}
              blurred={
                conversation.character.visibility === "private" &&
                subscription?.status !== "active"
              }
            />
          ))}
          <div ref={targetElementRef} />
          {(isLoadingConversations || isFetchingNextPageConversations) && (
            <>
              <ConversationsMenuItemSkeleton />
              <ConversationsMenuItemSkeleton />
              <ConversationsMenuItemSkeleton />
              <ConversationsMenuItemSkeleton />
              <ConversationsMenuItemSkeleton />
            </>
          )}
        </ScrollArea>
      )}
    </>
  );
}
